import React, { createContext, useContext, useEffect, useState } from 'react';
import { cloudSync } from '../services/sync/CloudSyncService';
import {
    User,
    signInWithPopup,
    signOut,
    onAuthStateChanged,
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    signInWithEmailAndPassword
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, Timestamp, updateDoc, serverTimestamp, collectionGroup, query, where, getDocs, onSnapshot as onSnapshotDoc, documentId } from 'firebase/firestore';
import { db, auth, googleProvider } from '../src/firebase';
import { userLogsService } from '../services/userLogsService';
import { queryClient } from '../services/queryClient';
import { useToast } from '../hooks/useToast';
import { removeKey, saveJSON } from '../services/storage';
import { DB_KEYS } from '../services/dbKeys';
import { SeasonSettings } from '../types';
import { useLicense } from '../src/hooks/useLicense';
import { License } from '../src/types/license';
import { OWNER_UID } from '../constants';

import { createTrialLicense } from '../src/services/licenseService';
import { subscribeToSystemSettings, SystemSettings } from '../services/api/systemSettings';

const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn';

interface FirebaseAuthContextType {
    user: User | null;
    license: License | null;
    subscriptionActive: boolean;
    loading: boolean;
    emailLinkSent: boolean;
    emailLinkError: string | null;
    login: () => Promise<void>;
    logout: () => Promise<void>;
    sendEmailLink: (email: string) => Promise<void>;
    signInWithEmail: (email: string, password: string) => Promise<void>;
    resetEmailLinkState: () => void;
    activateTrial: () => Promise<void>;
    tenantId: string | null;
    memberRole: string | null;
    memberRecord: any | null; // Added to access requiresPasswordChange
    rolePolicies: Record<string, string[]> | null;
    systemSettings: SystemSettings;
}

const FirebaseAuthContext = createContext<FirebaseAuthContextType | null>(null);

export const FirebaseAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [authLoading, setAuthLoading] = useState(true);
    const [emailLinkSent, setEmailLinkSent] = useState(false);
    const [emailLinkError, setEmailLinkError] = useState<string | null>(null);
    const [memberRecord, setMemberRecord] = useState<any | null>(null);
    const [rolePolicies, setRolePolicies] = useState<Record<string, string[]> | null>(null);
    const [memberLicense, setMemberLicense] = useState<License | null>(null);
    const [membershipLoading, setMembershipLoading] = useState(false);
    const [membershipChecked, setMembershipChecked] = useState(false);
    const [memberLicenseChecked, setMemberLicenseChecked] = useState(false);
    const [systemSettings, setSystemSettings] = useState<SystemSettings>({ trialModeEnabled: false });
    const [lastUserId, setLastUserId] = useState<string | null>(null);
    const { showToast } = useToast();

    // Synchronously reset checked states if user changed (prevent flicker)
    const currentUserId = user?.uid ?? null;
    if (currentUserId !== lastUserId) {
        setLastUserId(currentUserId);
        setMembershipChecked(false);
        setMemberLicenseChecked(false);
    }

    useEffect(() => {
        return subscribeToSystemSettings((settings) => {
            setSystemSettings(settings);
        });
    }, []);

    // Handle Email Link sign-in on mount
    useEffect(() => {
        const completeEmailSignIn = async () => {
            if (isSignInWithEmailLink(auth, window.location.href)) {
                let email = localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);

                if (!email) {
                    const urlParams = new URL(window.location.href).searchParams;
                    email = urlParams.get('email');
                }

                if (!email) {
                    email = window.prompt('Введите ваш email для завершения входа:');
                }

                if (email) {
                    try {
                        await signInWithEmailLink(auth, email, window.location.href);
                        localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
                        showToast('Вход выполнен успешно!', 'success');

                        if (window.history.replaceState) {
                            window.history.replaceState({}, document.title, window.location.pathname);
                        }
                    } catch (error: any) {
                        console.error('Email link sign-in error:', error);
                        setEmailLinkError(error.message || 'Ошибка входа по ссылке');
                        showToast('Ошибка входа по ссылке. Попробуйте снова.', 'error');
                    }
                }
            }
        };

        completeEmailSignIn();
    }, [showToast]);


    // Listen to Auth State
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            cloudSync.setUser(currentUser, undefined, null);
            setAuthLoading(false);
            if (currentUser) {
                setMembershipLoading(true);
                setMembershipChecked(false);
            }
        });
        return () => unsubscribe();
    }, []);

    // Listen to Personal License
    const { license: personalLicense, loading: personalLicenseLoading } = useLicense(user);

    // Session tracking: record login and update lastSeen
    useEffect(() => {
        if (!user || !personalLicense) return;

        const SESSION_START_KEY = 'sessionStartTime';

        const recordLogin = async () => {
            try {
                const docRef = doc(db, 'licenses', user.uid);
                userLogsService.logActivity('LOGIN', 'User logged in via Session Start', user);
                const now = Date.now();
                sessionStorage.setItem(SESSION_START_KEY, String(now));

                await updateDoc(docRef, {
                    lastLoginAt: serverTimestamp(),
                    lastSeen: serverTimestamp(),
                    displayName: user.displayName || '',
                    photoURL: user.photoURL || '',
                    email: user.email || ''
                });
            } catch (err: any) {
                if (err.code !== 'not-found') {
                    console.error("Login tracking error:", err);
                }
            }
        };

        const pulse = async () => {
            try {
                const docRef = doc(db, 'licenses', user.uid);
                await updateDoc(docRef, {
                    lastSeen: serverTimestamp()
                });
            } catch (err: any) {
                if (err.code !== 'not-found') {
                    console.error("Heartbeat error:", err);
                }
            }
        };

        const handleBeforeUnload = () => {
            const sessionStart = sessionStorage.getItem(SESSION_START_KEY);
            if (!sessionStart) return;

            const duration = Date.now() - parseInt(sessionStart, 10);
            const payload = JSON.stringify({
                uid: user.uid,
                duration,
                timestamp: Date.now()
            });

            localStorage.setItem('pendingLogout', payload);
        };

        const processPendingLogout = async () => {
            const pending = localStorage.getItem('pendingLogout');
            if (pending) {
                try {
                    const data = JSON.parse(pending);
                    const docRef = doc(db, 'licenses', data.uid);

                    await updateDoc(docRef, {
                        lastLogoutAt: Timestamp.fromMillis(data.timestamp),
                        lastSessionDuration: data.duration
                    });

                    localStorage.removeItem('pendingLogout');
                } catch (err) {
                    console.error("Process pending logout error:", err);
                }
            }
        };

        processPendingLogout();
        recordLogin();

        const interval = setInterval(pulse, 2 * 60 * 1000); // 2 minutes
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            clearInterval(interval);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [user, !!personalLicense]);

    // Effect to find membership and LISTEN for changes if no personal license
    useEffect(() => {
        if (!user || personalLicenseLoading) return;

        if (personalLicense) {
            setMemberRecord(null);
            setMemberLicense(null);
            setMembershipLoading(false);
            setMembershipChecked(true);
            return;
        }

        let unsubscribeMembership: (() => void) | null = null;

        const findMembership = async () => {
            setMembershipLoading(true);
            try {
                let membersSnap = await getDocs(query(collectionGroup(db, 'members'), where('uid', '==', user.uid)));

                if (membersSnap.empty && user.email) {
                    membersSnap = await getDocs(query(collectionGroup(db, 'members'), where('email', '==', user.email.toLowerCase())));
                }

                if (!membersSnap.empty) {
                    const firstDoc = membersSnap.docs[0];
                    unsubscribeMembership = onSnapshotDoc(firstDoc.ref, (docSnap) => {
                        if (docSnap.exists()) {
                            setMemberRecord({
                                ...(docSnap.data() as any),
                                id: docSnap.id,
                                tenantId: docSnap.ref.parent.parent?.id || null
                            });
                        } else {
                            setMemberRecord(null);
                        }
                        setMembershipLoading(false);
                        setMembershipChecked(true);
                    });
                } else {
                    setMemberRecord(null);
                    setMembershipLoading(false);
                    setMembershipChecked(true);
                }
            } catch (err) {
                console.error("[Auth] Membership lookup error:", err);
                setMembershipLoading(false);
                setMembershipChecked(true);
            }
        };

        findMembership();

        return () => {
            if (unsubscribeMembership) unsubscribeMembership();
        };
    }, [user, personalLicense, personalLicenseLoading]);

    // Derived Logic
    const prioritizeMember = !!memberRecord && (!personalLicense || personalLicense.status === 'trial');

    // Effect to sync organization Role Policies in real-time
    useEffect(() => {
        const tId = (personalLicense && !prioritizeMember) ? user?.uid : (memberRecord?.tenantId || null);
        if (!tId) {
            setRolePolicies(null);
            return;
        }

        const policiesRef = doc(db, 'organizations', tId, 'settings', 'role_policies');
        const unsubscribe = onSnapshotDoc(policiesRef, (snapshot) => {
            if (snapshot.exists()) {
                setRolePolicies(snapshot.data().policies || null);
            } else {
                setRolePolicies(null);
            }
        });

        return () => unsubscribe();
    }, [user?.uid, memberRecord?.tenantId, personalLicense, prioritizeMember]);

    // Sync Season Settings across organization (real-time)
    useEffect(() => {
        const tId = (personalLicense && !prioritizeMember) ? user?.uid : (memberRecord?.tenantId || null);
        if (!tId) return;

        const seasonRef = doc(db, 'organizations', tId, 'settings', 'season');
        const unsubscribe = onSnapshotDoc(seasonRef, async (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data() as SeasonSettings;
                await saveJSON(DB_KEYS.SEASON_SETTINGS, data);
                // Invalidate react-query cache so WaybillList/useWaybillForm pick up changes
                queryClient.invalidateQueries({ queryKey: ['seasonSettings'] });
            }
        });

        return () => unsubscribe();
    }, [user?.uid, memberRecord?.tenantId, personalLicense, prioritizeMember]);

    // Security Gate for Trial Registration
    useEffect(() => {
        if (authLoading || personalLicenseLoading || membershipLoading || !membershipChecked || !user) return;

        const enforceSecurityGate = async () => {
            const isSystemOwner = user.uid === OWNER_UID;
            if (isSystemOwner) return;

            if (memberRecord) return;

            if (personalLicense) {
                if (personalLicense.status === 'active') return;
                if (personalLicense.status === 'trial' && personalLicense.validTo) return;
            }

            if (!systemSettings.trialModeEnabled) {
                console.warn("[Auth] Security Gate: Redirecting new user due to disabled trial registration.");
                await signOut(auth);
                showToast('Регистрация временно ограничена администратором.', 'info');
            }
        };

        enforceSecurityGate();
    }, [user, personalLicense, memberRecord, membershipChecked, authLoading, personalLicenseLoading, membershipLoading, systemSettings.trialModeEnabled]);

    // Effect to sync license from tenant if user is a member
    useEffect(() => {
        if (!memberRecord?.tenantId) {
            setMemberLicense(null);
            setMemberLicenseChecked(true);
            return;
        }

        setMemberLicenseChecked(false);
        const tenantLicenseRef = doc(db, 'licenses', memberRecord.tenantId);
        const unsubscribe = onSnapshotDoc(tenantLicenseRef, (snapshot) => {
            if (snapshot.exists()) {
                setMemberLicense({ ...snapshot.data() as License, id: snapshot.id });
            } else {
                setMemberLicense(null);
            }
            setMemberLicenseChecked(true);
        }, (err) => {
            console.error("Member license sync error:", err);
            setMemberLicenseChecked(true);
        });

        return () => {
            unsubscribe();
        };
    }, [memberRecord?.tenantId]);

    const activeLicense = prioritizeMember ? memberLicense : (personalLicense || memberLicense);
    const tenantId = (personalLicense && !prioritizeMember) ? user?.uid : (memberRecord?.tenantId || null);
    const memberRole = prioritizeMember ? (memberRecord?.role || 'user') : (personalLicense ? 'admin' : (memberRecord?.role || 'user'));

    // Aggregated loading state: True if ANY part of auth/membership/license is still in flight
    // Crucially includes membershipChecked and memberLicenseChecked to wait until all async checks are done.
    const loading = authLoading || personalLicenseLoading || membershipLoading || (!!user && !membershipChecked) || (!!memberRecord && !memberLicenseChecked);

    const subscriptionActive = !!activeLicense &&
        (activeLicense.status === 'active' || activeLicense.status === 'trial') &&
        (activeLicense.validTo
            ? activeLicense.validTo.toMillis() > Date.now()
            : (activeLicense.status === 'active'));

    // Yandex.Metrika Dynamic Initialization
    useEffect(() => {
        if (authLoading || personalLicenseLoading || membershipLoading || !membershipChecked || !user) return;

        const initMetrica = () => {
            if (typeof (window as any).ym !== 'function') {
                setTimeout(initMetrica, 1000);
                return;
            }

            const webvisorEnabled = activeLicense?.webvisorEnabled !== false && memberRecord?.webvisorEnabled !== false;

            console.log("[Metrica] Initializing with webvisor:", webvisorEnabled);

            (window as any).ym(105781265, 'init', {
                clickmap: true,
                trackLinks: true,
                accurateTrackBounce: true,
                webvisor: webvisorEnabled,
                ecommerce: "dataLayer"
            });
        };

        initMetrica();
    }, [user, loading, activeLicense?.webvisorEnabled, memberRecord?.webvisorEnabled]);

    // Update CloudSync with tenantId and role
    useEffect(() => {
        if (tenantId) {
            cloudSync.setTenantId(tenantId);
        }
        if (memberRole) {
            cloudSync.setRole(memberRole);
        }
    }, [tenantId, memberRole]);

    const login = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const loggedUser = result.user;

            if (loggedUser) {
                const ownerSnap = await getDoc(doc(db, 'licenses', loggedUser.uid));
                const memberSnapById = await getDocs(query(collectionGroup(db, 'members'), where('uid', '==', loggedUser.uid)));
                const memberSnapByEmail = await getDocs(query(collectionGroup(db, 'members'), where('email', '==', loggedUser.email?.toLowerCase())));

                if (!ownerSnap.exists() && memberSnapById.empty && memberSnapByEmail.empty) {
                    if (!systemSettings.trialModeEnabled) {
                        await signOut(auth);
                        showToast('Регистрация временно ограничена администратором.', 'error');
                        return;
                    }

                    await signOut(auth);
                    showToast('Доступ запрещен. Обратитесь к администратору для регистрации.', 'error');
                    return;
                }

                if (ownerSnap.exists()) {
                    const lData = ownerSnap.data() as any;
                    const hasNoOrgs = !lData.organizations || lData.organizations.length === 0;
                    if (hasNoOrgs && !systemSettings.trialModeEnabled && lData.status !== 'active') {
                        await signOut(auth);
                        showToast('Регистрация временно ограничена администратором.', 'error');
                        return;
                    }
                }
            }
        } catch (error) {
            console.error("Ошибка входа:", error);
            showToast('Ошибка входа', 'error');
        }
    };

    const activateTrial = async () => {
        if (!user) return;
        if (!systemSettings.trialModeEnabled) {
            showToast('Активация пробного периода временно отключена администратором', 'info');
            return;
        }
        try {
            await createTrialLicense(user, 'Моя организация');
            showToast('Пробный период активирован!', 'success');
        } catch (error: any) {
            console.error('Trial activation error:', error);
            showToast('Ошибка активации пробного периода', 'error');
        }
    };

    const sendEmailLink = async (email: string) => {
        const actionCodeSettings = {
            url: `${window.location.origin}?email=${encodeURIComponent(email)}`,
            handleCodeInApp: true,
        };

        try {
            setEmailLinkError(null);
            await sendSignInLinkToEmail(auth, email, actionCodeSettings);
            localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
            setEmailLinkSent(true);
            showToast(`Ссылка для входа отправлена на ${email}`, 'success');
        } catch (error: any) {
            console.error('Send email link error:', error);
            setEmailLinkError(error.message || 'Ошибка отправки ссылки');
            showToast('Ошибка отправки ссылки. Проверьте email.', 'error');
        }
    };

    const signInWithEmail = async (email: string, password: string) => {
        try {
            setEmailLinkError(null);
            cloudSync.setPreloadedPassphrase(password);

            await signInWithEmailAndPassword(auth, email, password);
            userLogsService.logActivity('LOGIN', 'User logged in via Email/Password', { email } as any);
            showToast('Вход выполнен успешно!', 'success');
        } catch (error: any) {
            cloudSync.setPreloadedPassphrase(null);
            let message = 'Ошибка входа. Проверьте Email и пароль.';
            if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
                message = 'Неверный Email или пароль.';
            } else if (error.code === 'auth/too-many-requests') {
                message = 'Слишком много попыток. Попробуйте позже.';
            }
            setEmailLinkError(message);
            showToast(message, 'error');
            throw error;
        }
    };

    const resetEmailLinkState = () => {
        setEmailLinkSent(false);
        setEmailLinkError(null);
    };

    const logout = async () => {
        try {
            if (user) {
                const sessionStart = sessionStorage.getItem('sessionStartTime');
                if (sessionStart) {
                    const duration = Date.now() - parseInt(sessionStart, 10);
                    const docRef = doc(db, 'licenses', user.uid);

                    await updateDoc(docRef, {
                        lastLogoutAt: serverTimestamp(),
                        lastSessionDuration: duration
                    });

                    sessionStorage.removeItem('sessionStartTime');
                    localStorage.removeItem('pendingLogout');
                }
            }

            await signOut(auth);
            queryClient.clear();
            await removeKey('__current_user__');
            localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
            showToast('Вы вышли из аккаунта', 'success');
            userLogsService.logActivity('LOGOUT', 'User performed logout', user);
        } catch (error) {
            console.error('[auth] logout failed', error);
            showToast('Ошибка при выходе', 'error');
        }
    };

    return (
        <FirebaseAuthContext.Provider value={{
            user,
            license: activeLicense,
            subscriptionActive,
            loading,
            emailLinkSent,
            emailLinkError,
            login,
            logout,
            sendEmailLink,
            signInWithEmail,
            resetEmailLinkState,
            activateTrial,
            tenantId,
            memberRole,
            memberRecord,
            rolePolicies,
            systemSettings
        }}>
            {children}
        </FirebaseAuthContext.Provider>
    );
};

export const useFirebaseAuth = () => {
    const context = useContext(FirebaseAuthContext);
    if (!context) throw new Error("useFirebaseAuth must be used within FirebaseAuthProvider");
    return context;
};
